import React, { useMemo } from "react";
import { CacheProvider, EmotionCache } from "@emotion/react";
import createCache from "@emotion/cache";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from "@mui/material/styles";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import { useI18n } from "../languages/providers/I18nProvider";

/**
 * Emotion caches. The RTL cache runs the stylis RTL plugin so that every style
 * authored through MUI's `sx`/`styled` (which use Emotion) has its physical
 * left/right properties mirrored automatically when the document is RTL.
 *
 * They are created once at module scope so the inserted <style> tags are reused
 * across renders rather than recreated on every language switch.
 */
const ltrCache: EmotionCache = createCache({
  key: "mui",
  stylisPlugins: [prefixer],
});

const rtlCache: EmotionCache = createCache({
  key: "mui-rtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

/**
 * Provides a direction-aware MUI theme + Emotion cache. Must live inside
 * I18nProvider so it can follow the active language's direction.
 */
export const MuiRtlProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { direction } = useI18n();

  const theme = useMemo(() => createTheme({ direction }), [direction]);
  const cache = direction === "rtl" ? rtlCache : ltrCache;

  return (
    <CacheProvider value={cache}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </CacheProvider>
  );
};

export default MuiRtlProvider;
